import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Observable} from 'rxjs';
import {filter, timeout} from 'rxjs/operators';

/**
 * Subscription Coordinator Service
 *
 * FIN-0079: Coordinates WebSocket subscription readiness to eliminate race condition.
 *
 * Purpose:
 * - Provides a shared abstraction for subscription initialization
 * - Services register initialization callbacks during construction
 * - ConnectionOrchestrator triggers all initializers before emitting CONNECTED
 * - Maintains SOLID principles (Dependency Inversion, Single Responsibility)
 *
 * Usage Pattern (Callback Registration):
 * 1. Services register initializers: subscriptionCoordinator.registerInitializer('name', fn)
 * 2. ConnectionOrchestrator triggers: await subscriptionCoordinator.runAllInitializers()
 * 3. Coordinator marks subscriptions ready after all initializers complete
 *
 * Architecture:
 *   SubscriptionCoordinator (shared abstraction)
 *       ↑                ↑
 *       |                |
 *   ConnectionOrchestrator  ChatCommunicationService (and other services)
 */
@Injectable({
  providedIn: 'root'
})
export class SubscriptionCoordinator {
  private subscriptionsReadySubject = new BehaviorSubject<boolean>(false);
  private initializers: Array<{name: string, fn: () => Promise<void>}> = [];

  /**
   * Observable that emits true when subscriptions are ready
   */
  public subscriptionsReady$: Observable<boolean> = this.subscriptionsReadySubject.asObservable();

  /**
   * Signal that subscriptions are ready
   * Called by ChatCommunicationService after subscription setup completes
   */
  markSubscriptionsReady(): void {
    this.subscriptionsReadySubject.next(true);
  }

  /**
   * Signal that subscriptions are no longer valid
   * Called when connection is lost or during intentional disconnect
   */
  resetSubscriptions(): void {
    this.subscriptionsReadySubject.next(false);
  }

  /**
   * Check if subscriptions are currently ready (synchronous)
   */
  areSubscriptionsReady(): boolean {
    return this.subscriptionsReadySubject.value;
  }

  /**
   * Wait for subscriptions to be ready
   *
   * @param timeoutMs Timeout in milliseconds (default: 10000ms)
   * @returns Promise that resolves when subscriptions are ready
   * @throws Error if timeout is reached before subscriptions are ready
   */
  async waitForSubscriptionsReady(timeoutMs: number = 10000): Promise<void> {
    // If already ready, return immediately
    if (this.subscriptionsReadySubject.value === true) {
      return Promise.resolve();
    }

    // Wait for subscriptions to become ready
    return firstValueFrom(
      this.subscriptionsReady$.pipe(
        filter(ready => ready === true),
        timeout(timeoutMs)
      )
    ).then(() => {
      // Promise<boolean> → Promise<void>
    });
  }

  /**
   * Register an initializer callback
   * Services call this during their initialization to register setup tasks
   * that must complete before connection is marked as CONNECTED
   *
   * @param name Human-readable name for logging/debugging
   * @param fn Async function that performs initialization
   */
  registerInitializer(name: string, fn: () => Promise<void>): void {
    console.log(`[SubscriptionCoordinator] Registered initializer: ${name}`);
    this.initializers.push({name, fn});
  }

  /**
   * Run all registered initializers sequentially
   * Called by ConnectionOrchestrator after WebSocket transport is established
   * Marks subscriptions as ready after all initializers complete
   *
   * @returns Promise that resolves when all initializers complete
   * @throws Error if any initializer fails
   */
  async runAllInitializers(): Promise<void> {
    console.log(`[SubscriptionCoordinator] Running ${this.initializers.length} initializers...`);

    for (const {name, fn} of this.initializers) {
      console.log(`[SubscriptionCoordinator] Running initializer: ${name}`);
      const start = Date.now();

      try {
        await fn();
        const duration = Date.now() - start;
        console.log(`[SubscriptionCoordinator] ✅ Initializer '${name}' completed in ${duration}ms`);
      } catch (error) {
        console.error(`[SubscriptionCoordinator] ❌ Initializer '${name}' failed:`, error);
        throw error; // Fail fast - don't mark ready if any initializer fails
      }
    }

    // All initializers completed successfully
    this.markSubscriptionsReady();
    console.log(`[SubscriptionCoordinator] ✅ All initializers completed, subscriptions marked ready`);
  }

  /**
   * Clear all registered initializers
   * Useful for testing or when starting fresh after disconnect
   */
  clearInitializers(): void {
    console.log(`[SubscriptionCoordinator] Clearing ${this.initializers.length} initializers`);
    this.initializers = [];
  }
}
